import { useState, useRef, useEffect } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Heading,
  useNumberInput,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  IconButton,
  Flex,
  keyframes,
} from "@chakra-ui/react";
import {
  CollecticonChevronDownSmall,
  CollecticonChevronUpSmall,
  CollecticonTickSmall,
  CollecticonXmarkSmall,
  CollecticonFlagPole,
} from "@devseed-ui/collecticons-chakra";
import { useQueryClient } from "@tanstack/react-query";

import { Loading, Error, AutoSaveTextArea } from "../../components";
import MetaDataCard, {
  MetaData,
  MetaDataHeader,
} from "../../components/MetaDataCard";
import Panel, { PanelBody } from "../../components/Panel";
import { ValidatedBadge } from "../../components/DetectionCard";
import { Inference } from "../../types/inferences";
import { Feature } from "geojson";
import { formatDate, getSpeciesColor, getSpeciesLabel } from "../../utils";
import ReclassificationOptions from "./ReclassificationOptions";
import { getInferenceId, getCompositeIds } from "./inferenceHelpers";
import * as API from "../../api/inferences";

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;
const fadeInAnimation = `${fadeIn} 0.3s ease-in`;

type ValidationPanelProps = {
  borderColor: string;
  scene: Feature;
  sceneDate: string;
  model: { name: string; version: string } | undefined;
  selectedInference: Inference;
  species: string;
  confidence: string;
  validate: (options: {
    validationId: number;
    isValid: boolean;
    compositeIds?: number[];
  }) => Promise<void> | undefined;
  isValidating: boolean;
  isValidated: boolean;
  validationError: string | undefined;
  updateInference: (options: {
    inferenceId: number;
    notes?: string;
    selected_class?: string | null;
    flagged?: boolean;
    compositeIds?: number[];
  }) => Promise<void> | undefined;
  isUpdating:boolean;
  updateError: string | undefined;
  compositeMode: boolean;
};
function ValidationPanel({
  borderColor,
  scene,
  sceneDate,
  model,
  selectedInference,
  species,
  confidence,
  validate,
  isValidating,
  isValidated,
  validationError,
  updateInference,
  isUpdating,
  updateError,
  compositeMode,
}: ValidationPanelProps) {
  const queryClient = useQueryClient();
  const [showLoader, setShowLoader] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  // Helper to get default composite count
  const getDefaultCompositeCount = () => {
    const count = selectedInference.properties.composite_count;
    if (count !== null && count !== undefined) return count;
    // Fallback to the number of IDs in the composite
    const idsLength = selectedInference.properties.inference_ids?.length ||
                      1;
    return idsLength;
  };

  const [compositeCount, setCompositeCount] = useState<number>(getDefaultCompositeCount());
  const [isSavingCount, setIsSavingCount] = useState(false);
  const [isCountSaved, setIsCountSaved] = useState(false);
  const countSavedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const savedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update composite count when composite changes
  useEffect(() => {
    const count = selectedInference.properties.composite_count;

    if (count !== null && count !== undefined) {
      setCompositeCount(count);
    } else {
      // Fallback to the number of IDs in the composite
      const fallbackCount = selectedInference.properties.ids?.length ||
                           selectedInference.properties.inference_ids?.length ||
                           1;
      setCompositeCount(fallbackCount);
    }
  }, [selectedInference.properties.composite_id, selectedInference.properties.composite_count, selectedInference.properties.ids, selectedInference.properties.inference_ids]);

  // Save composite count with debounce
  const saveCompositeCount = (newCount: number) => {
    const ids = selectedInference.properties.ids;
    const sceneProperties = scene.properties;
    if (!compositeMode || !ids || !sceneProperties) return;

    // Clear any pending save
    if (countSaveTimeoutRef.current) {
      clearTimeout(countSaveTimeoutRef.current);
    }

    // Debounce the save for 1 second
    countSaveTimeoutRef.current = setTimeout(() => {
      setIsSavingCount(true);
      API.batchUpdateInferences(
        sceneProperties.s3_prefix,
        {
          ids,
          composite_count: newCount
        }
      )
        .then(() => {
          // Invalidate the query cache to refetch with updated composite_count
          queryClient.invalidateQueries({
            queryKey: ["scene-inferences", sceneProperties.s3_prefix]
          });
          setIsCountSaved(true);
          if (countSavedTimeoutRef.current) clearTimeout(countSavedTimeoutRef.current);
          countSavedTimeoutRef.current = setTimeout(() => setIsCountSaved(false), 4000);
        })
        .catch(() => {
          // Error handled silently
        })
        .finally(() => {
          setIsSavingCount(false);
        });
    }, 1000);
  };

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      value: compositeCount,
      min: 0,
      max: 100,
      onChange: (valueAsString, valueAsNumber) => {
        setCompositeCount(valueAsNumber);
        saveCompositeCount(valueAsNumber);
      },
    });
  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  // Watch for isUpdating changes to control loader display
  useEffect(() => {
    if (isUpdating) {
      setShowLoader(true);
      // Ensure loader is shown for at least 2 seconds
      if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current);
      loaderTimeoutRef.current = setTimeout(() => {
        setShowLoader(false);
      }, 2000);
    } else {
      // If update finishes before 2s, wait for timeout to hide loader
      if (loaderTimeoutRef.current) {
        setTimeout(() => setShowLoader(false), 2000);
      } else {
        setShowLoader(false);
      }
    }
    // Cleanup on unmount
    return () => {
      if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current);
    };
  }, [isUpdating]);

  // Cleanup composite count save timeout on unmount
  useEffect(() => {
    return () => {
      if (countSaveTimeoutRef.current) clearTimeout(countSaveTimeoutRef.current);
    };
  }, []);

  // Handler for when save completes
  const handleSaveComplete = () => {
    setShowSaved(true);
    if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    savedTimeoutRef.current = setTimeout(() => setShowSaved(false), 6000);
  };

  return (
    <Panel>
      <PanelBody>
        <Box
          display="flex"
          gap="4"
          pb="4"
          borderBottom="1px solid"
          borderColor={borderColor}
          alignItems="center"
        >
          <Heading size="md" as="h2" flex="1">
            Validate Detection
          </Heading>
          {isValidated && (
            <ValidatedBadge isValid={selectedInference.properties.is_valid} />
          )}
          <IconButton
            aria-label={selectedInference.properties.flagged ? "Unflag detection" : "Flag detection"}
            title={selectedInference.properties.flagged ? "Detection flagged" : "Flag detection"}
            icon={<CollecticonFlagPole />}
            size="sm"
            variant={selectedInference.properties.flagged ? "solid" : "outline"}
            colorScheme={selectedInference.properties.flagged ? "orange" : "gray"}
            onClick={() => updateInference({
              inferenceId: getInferenceId(selectedInference, compositeMode),
              flagged: !selectedInference.properties.flagged,
              compositeIds: getCompositeIds(selectedInference, compositeMode)
            })}
            isLoading={isUpdating}
            isDisabled={isUpdating}
          />
        </Box>
        <MetaDataCard>
          <MetaDataHeader as="h3">Scene Metadata</MetaDataHeader>
          <MetaData
            label="Scene Id"
            value={`${scene?.properties?.s3_prefix.substring(0, 20)}...`}
          />
          <MetaData label="Aquired" value={sceneDate} />
          <MetaData
            label="Uploaded"
            value={formatDate(new Date(Date.parse(scene?.properties?.registered))).slice(0, 20)}
          />
        </MetaDataCard>
        {compositeMode && (
          <MetaDataCard>
            <Flex justifyContent="space-between" alignItems="center">
              <MetaDataHeader as="h3">Composite Details</MetaDataHeader>
              {(isSavingCount || isCountSaved) && (
                <Box
                  key={isSavingCount ? "saving" : "saved"}
                  fontSize="xs"
                  color="primary.500"
                  opacity={0.75}
                  lineHeight="1"
                  animation={fadeInAnimation}
                >
                  {isSavingCount ? "Saving count..." : "Count saved"}
                </Box>
              )}
            </Flex>
            <Box display="flex" fontSize="sm" gap="4" alignItems="center">
              <Box as="span" opacity="0.875" flex="1">
                Individuals Present
              </Box>
              <InputGroup
                maxW="max-content"
                border="1px solid"
                borderRadius="md"
                size="sm"
              >
                <InputLeftAddon
                  {...dec}
                  as={Button}
                  variant="base-outline"
                  bg="transparent"
                  borderRadius="md"
                >
                  <CollecticonChevronDownSmall />
                </InputLeftAddon>
                <Input {...input} width="3rem" p={0} textAlign="center" />
                <InputRightAddon
                  {...inc}
                  as={Button}
                  variant="base-outline"
                  bg="transparent"
                  borderRadius="md"
                >
                  <CollecticonChevronUpSmall />
                </InputRightAddon>
              </InputGroup>
            </Box>
            <MetaData
              label="Composite Id"
              value={selectedInference.properties.composite_id || "N/A"}
            />
            <MetaData
              label="Chip Count"
              value={selectedInference.properties.inference_ids?.length || "N/A"}
            />
            <Accordion allowToggle>
              <AccordionItem border="0.5px solid" borderColor="primary.500" borderRadius="md" mt="2" p={2} bg="primary.100a">
                <Box as="h3">
                  <AccordionButton display="flex" alignItems="center" justifyContent="space-between" m="0" p="0" fontSize="sm">
                    <strong>What is composite mode?</strong>
                    <AccordionIcon />
                  </AccordionButton>
                </Box>
                <AccordionPanel p={0} mt={2} lineHeight="1.4" fontSize="sm">
                  {`This “Composite view” combines ${selectedInference.properties.composite_count ?? 1} inference${(selectedInference.properties.composite_count ?? 1) > 1 ? "s" : ""}. To remove any members of the composite, invalidate individually, or label other members as a different class, exit “Composite” mode via the toggle above.`}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </MetaDataCard>
        )}
        <MetaDataCard>
          <MetaDataHeader as="h3">Detection Details</MetaDataHeader>
          <Box display="flex" fontSize="sm" gap="1" alignItems="baseline">
            <Box as="span" opacity="0.875" flex="1">
              Species
            </Box>
            <Box
              bgColor={getSpeciesColor(species)}
              borderRadius="full"
              height="3"
              minWidth="3"
            />
            <Box as="span" fontWeight="500">
              {getSpeciesLabel(species)}
            </Box>
          </Box>
          <MetaData label="Confidence" value={confidence} valueAsTag />
          <MetaData
            label="Model"
            value={`${model?.name} (${model?.version})`}
          />
          <MetaData
            label="ymin"
            value={selectedInference.properties.inference.pixel_coords.ymin}
          />
          <MetaData
            label="xmin"
            value={selectedInference.properties.inference.pixel_coords.xmin}
          />
          <MetaData
            label="ymax"
            value={selectedInference.properties.inference.pixel_coords.ymax}
          />
          <MetaData
            label="xmax"
            value={selectedInference.properties.inference.pixel_coords.xmax}
          />
        </MetaDataCard>
        <ButtonGroup width="100%">
          <Button
            flex="1"
            leftIcon={<CollecticonXmarkSmall />}
            onClick={() => validate({
              validationId: getInferenceId(selectedInference, compositeMode),
              isValid: false,
              compositeIds: getCompositeIds(selectedInference, compositeMode)
            })}
            isDisabled={isValidating}
            isLoading={isValidating}
            colorScheme="red"
            variant={
              selectedInference.properties.is_valid === false
                ? "solid"
                : "outline"
            }
          >
            Invalid
          </Button>
          <Button
            flex="1"
            leftIcon={<CollecticonTickSmall />}
            onClick={() => {
              validate({
                validationId: getInferenceId(selectedInference, compositeMode),
                isValid: true,
                compositeIds: getCompositeIds(selectedInference, compositeMode)
              });
              updateInference({
                inferenceId: getInferenceId(selectedInference, compositeMode),
                selected_class: null,
                compositeIds: getCompositeIds(selectedInference, compositeMode)
              });
            }}
            isDisabled={isValidating}
            isLoading={isValidating}
            colorScheme="green"
            variant={
              selectedInference.properties.is_valid === true
                ? "solid"
                : "outline"
            }
          >
            Valid
          </Button>
        </ButtonGroup>
        {isValidating && (
          <Loading
            display="flex"
            gap="2"
            justifyContent="center"
            fontSize="sm"
            opacity="0.75"
          >
            Updating validation status...
          </Loading>
        )}
        {validationError && (
          <Error
            display="flex"
            gap="2"
            fontWeight="normal"
            justifyContent="center"
            iconSize={6}
          >
            {" "}
            {validationError}
          </Error>
        )}
        <Accordion defaultIndex={[0]} allowMultiple>
          {!compositeMode && (
            <AccordionItem>
              <h2>
                <AccordionButton>
                  <MetaDataHeader as="h3" flex="1" textAlign="left">
                    Manual Label
                  </MetaDataHeader>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <ReclassificationOptions
                  key={
                    compositeMode
                      ? selectedInference.properties.composite_id
                      : selectedInference.properties.id
                  }
                  options={["Gray", "Humpback", "Unknown", "Other"]}
                  onChange={(value: string) =>
                    updateInference({
                      inferenceId: getInferenceId(selectedInference, compositeMode),
                      selected_class: value,
                      compositeIds: getCompositeIds(selectedInference, compositeMode)
                    })}
                  initialValue={
                    selectedInference.properties.selected_class || undefined
                  }
                  disabled={
                    isUpdating || selectedInference.properties.is_valid === true
                  }
                  selectedInference={selectedInference}
                  validate={validate}
                  predictedSpecies={species}
                />
              </AccordionPanel>
            </AccordionItem>
          )}
          <AccordionItem>
            <h2>
              <AccordionButton>
                <MetaDataHeader as="h3" flex="1" textAlign="left">
                  Notes
                </MetaDataHeader>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              {selectedInference && (
                <AutoSaveTextArea
                  key={
                    compositeMode
                      ? selectedInference.properties.composite_id
                      : selectedInference.properties.id
                  }
                  onSave={(value: string) =>
                    updateInference({
                      inferenceId: getInferenceId(selectedInference, compositeMode),
                      notes: value,
                      compositeIds: getCompositeIds(selectedInference, compositeMode)
                    })}
                  initialValue={selectedInference.properties.notes || ""}
                  disabled={isUpdating}
                  onSaveComplete={handleSaveComplete}
                />
              )}
              {showLoader && (
                <Loading
                  display="flex"
                  gap="2"
                  justifyContent="center"
                  fontSize="xs"
                  mt={2}
                  opacity="0.75"
                >
                  Updating detection notes...
                </Loading>
              )}
              {showSaved && !showLoader && !updateError && (
                <Box fontSize="xs" color="green.500" mt={2} opacity={0.75} lineHeight="1" animation={fadeInAnimation}>
                  Notes saved
                </Box>
              )}
              {updateError && (
                <Error
                  display="flex"
                  gap="2"
                  fontWeight="normal"
                  justifyContent="center"
                  iconSize={6}
                >
                  {" "}
                  {updateError}
                </Error>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </PanelBody>
    </Panel>
  );
}

export default ValidationPanel;
